/**
 * EmployeeSkillsLearningPage — the Skills & Learning workspace of one Digital Employee
 * (DIGEMP-SKILLS-LEARNING-V2-FRONTEND-085; the declared-vs-measured visual split and the CV
 * deep link added by DIGEMP-PRODUCTIZATION-GROWTH-UX-112).
 *
 * Route: /agents/:id/skills-learning
 *
 * One read-only operator surface that keeps six things visibly separate, because each answers a
 * different question and only some of them are evidence:
 *   1. מאושר (approved & entitled) — the tool/connection capability set the platform actually
 *      grants the employee (workspace capabilities projection), each tool carrying its existing
 *      approval-gate label.
 *   2. מוצהר (declared) — specialties/expertise the employee claims. Declarations are NOT
 *      permissions, NOT verification and are never merged into the approved counts.
 *   3. גרסאות והערכה (versions & evaluation) — the latest evaluation snapshot from run history,
 *      plus the admin-gated detailed evaluation report, plus the versioned skill history carried
 *      on the agent detail response (`learning_history`): per-domain state and version numbers,
 *      the append-only change log (evaluation / promotion / regression / suspension / reinstated /
 *      approved knowledge) with each change's own reason, evidence and provenance ids, so an
 *      operator can trace why the evaluated proficiency changed.
 *   4. עדויות מיומנות (proficiency evidence) — the run-based evidence counters the score derives
 *      from, plus the existing ExperienceTimeline component over the recorded run window.
 *   5. מועמדים לידע (knowledge candidates) — bounded lessons recorded against runs (technician
 *      corrections, failure/rejection notes, verified resolutions), each linking to its run and
 *      ticket; org-level candidates stay on the /knowledge page.
 *   6. יעדי למידה (learning goals) and נסיגות (regressions/rollback) — knowledge gaps tracked as
 *      goals, the versioned skill-state regressions/suspensions from the learning history, and
 *      failed/rejected work with stopped proposed actions, which are the platform's actual
 *      work-level correction/recovery trail.
 *
 * Access: the same agents_view gate as /agents/:id — every source this page reads already enforces
 * that gate server-side. The detailed skill evaluation is additionally admin/owner-only; when it
 * is denied the page degrades to an explicit note and everything else keeps rendering. Nothing
 * here is writable: no action, approval, entitlement or goal-state control is presented.
 *
 * Truthfulness rules (mirrors the sibling V2 compositions):
 *   - A failed/absent source renders "לא ידוע" (or an explicit note), never a fabricated 0.
 *   - Null scores render "טרם ידוע"/"—" with the reason (no terminal evidence), never 0%.
 *   - Free text and machine identifiers are bounded and rendered as plain text; raw payloads,
 *     credentials, tool arguments or JSON are never rendered.
 */
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, agentRunsApi, skillsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META, drawnStatus } from "../components/EmployeeAvatar";
import { ExperienceTimeline, OUTCOME_META, TOOL_SENSITIVITY } from "../components/EmployeeWorkspace";
import {
  SkillChangeEntry,
  SkillVersionHistorySection,
  normalizeVersionedSkillHistory,
} from "../components/LearningTimeline";

// Bounded windows: the experience history the page reads and the run window the evidence timeline
// renders. Hitting a cap is stated in the UI instead of silently pretending completeness.
export const EXPERIENCE_LIMIT = 50;
export const EVIDENCE_RUNS_LIMIT = 25;

// Bounded rendered lists — each says how many of how many are shown.
export const REGRESSION_MAX = 10;
export const CANDIDATE_MAX = 12;
export const TOOL_MAX = 18;
export const CONNECTION_MAX = 6;
export const SKILL_STATE_MAX = 10;

export const UNKNOWN = "לא ידוע";

export const LEARNING_STATUS_LABELS = {
  open: "פתוח",
  acknowledged: "אושר",
};

export const EVALUATION_BASIS_LABELS = {
  agent_run_history: "היסטוריית ריצות",
};

export const LESSON_KIND_META = {
  verified_resolution: { label: "פתרון מאומת", cls: "bg-green-100 text-green-700" },
  technician_correction: { label: "תיקון טכנאי", cls: "bg-violet-100 text-violet-700" },
  failure_note: { label: "הערת כשל", cls: "bg-red-100 text-red-700" },
  rejection_note: { label: "הערת דחייה", cls: "bg-slate-200 text-slate-700" },
};

const RUN_LABELS /* per run outcome vocabulary */ = {
  failed: "נכשל",
  cancelled: "נדחה",
  verified_success: "מאומת",
  unverified: "טרם אומת",
};

// The experience-history outcome vocabulary onto the shared run-outcome labels (same mapping the
// sibling growth/CV composition uses).
const ENTRY_OUTCOME_META = {
  verified_success: OUTCOME_META.verified,
  unverified: OUTCOME_META.unverified,
  failed: OUTCOME_META.failed,
  cancelled: OUTCOME_META.cancelled,
};

// ─── Pure helpers (exported for focused tests) ───────────────────────────────

export function boundedText(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

export function fmt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("he-IL");
  } catch {
    return null;
  }
}

// Rates from the backend are 0-1; overall_score is 0-100. Null/absent stays null (unknown),
// never 0.
export function formatRate(value) {
  return typeof value === "number" && Number.isFinite(value) ? `${Math.round(value * 100)}%` : null;
}

export function formatScore(value) {
  return typeof value === "number" && Number.isFinite(value) ? `${Math.round(value)} מתוך 100` : null;
}

export function toolGroups(tools) {
  const list = Array.isArray(tools) ? tools : [];
  return TOOL_SENSITIVITY.map((s) => ({
    key: s.key,
    label: s.label,
    title: s.title,
    chip: s.chip,
    tools: list.filter(s.match),
  }));
}

export function stoppedActions(entry) {
  const actions = entry?.actions;
  if (!actions || typeof actions !== "object") return 0;
  const failed = Number.isInteger(actions.failed) ? actions.failed : 0;
  const rejected = Number.isInteger(actions.rejected) ? actions.rejected : 0;
  return failed + rejected;
}

export function entryTimestamp(entry) {
  return entry?.occurred_at || null;
}

// Entry ids are recorded as "<source>:<id>" (e.g. "run:<uuid>"); only a run-prefixed id becomes
// a run link — anything else is left unlinked rather than guessed.
export function runIdOf(entry) {
  const entryId = boundedText(entry?.entry_id, 80);
  if (!entryId || !entryId.startsWith("run:")) return null;
  const runId = entryId.slice(4).trim();
  return runId || null;
}

// Regressions = recorded negative outcomes (failed rejected/abandoned work) plus positive work
// whose proposed actions were stopped (failed/rejected). This is the platform's actual
// correction/recovery trail — there is no skill-version regression ledger.
export function regressionEntries(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return list
    .filter((e) => e && (e.outcome === "failed" || e.outcome === "cancelled" || stoppedActions(e) > 0))
    .slice()
    .sort((a, b) => String(entryTimestamp(b) || "").localeCompare(String(entryTimestamp(a) || "")));
}

export function knowledgeCandidates(entries) {
  const out = [];
  for (const entry of Array.isArray(entries) ? entries : []) {
    for (const lesson of Array.isArray(entry?.lessons) ? entry.lessons : []) {
      if (lesson && typeof lesson === "object") out.push({ entry, lesson });
    }
  }
  return out;
}

export function learningStatusMeta(status) {
  const text = boundedText(status, 40);
  if (!text) return { label: UNKNOWN, title: undefined };
  if (LEARNING_STATUS_LABELS[text]) return { label: LEARNING_STATUS_LABELS[text], title: undefined };
  return { label: text, title: "הסטטוס כפי שדווח על ידי השרת" };
}

// ─── Small presentational pieces ─────────────────────────────────────────────

function UnknownNote({ children }) {
  return (
    <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
      {children || `${UNKNOWN} — המקור לא נטען.`}
    </p>
  );
}

function Section({ icon, label, hint, children, count }) {
  return (
    <section aria-label={label} className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span aria-hidden="true">{icon}</span>
        <h2 className="text-base font-semibold text-gray-800">{label}</h2>
        {count != null && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{count}</span>
        )}
      </div>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {children}
    </section>
  );
}

function Tile({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-3 min-w-[150px] flex-1">
      <div className="text-2xl font-semibold text-gray-800">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function MachineName({ children }) {
  return (
    <span dir="ltr" className="font-mono text-[11px]">
      {children}
    </span>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function EmployeeSkillsLearningPage() {
  const { id } = useParams();
  const { can, loading: permsLoading } = useAgentPermissions();
  const canView = can("agents_view");

  const agentQuery = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id && canView,
  });

  const profileQuery = useQuery({
    queryKey: ["agent-growth-profile", id],
    queryFn: () => agentsApi.growthProfile(id),
    enabled: !!id && canView,
    retry: false,
  });

  const experienceQuery = useQuery({
    queryKey: ["agent-experience-history", id, EXPERIENCE_LIMIT],
    queryFn: () => agentsApi.experienceHistory(id, { limit: EXPERIENCE_LIMIT }),
    enabled: !!id && canView,
    retry: false,
  });

  const workspaceQuery = useQuery({
    queryKey: ["agent-workspace", id],
    queryFn: () => agentsApi.workspace(id),
    enabled: !!id && canView,
    retry: false,
  });

  const runsQuery = useQuery({
    queryKey: ["agentRuns", "agent", id, "skills-learning"],
    queryFn: () => agentRunsApi.listByAgent(id, { limit: EVIDENCE_RUNS_LIMIT }),
    enabled: !!id && canView,
  });

  // Admin/owner-only server-side; a non-admin session gets 403 and the page degrades truthfully.
  const evaluationQuery = useQuery({
    queryKey: ["agent-skill-evaluation", id],
    queryFn: () => skillsApi.agentEvaluation(id),
    enabled: !!id && canView,
    retry: false,
  });

  if (permsLoading) {
    return <div className="p-6 text-gray-500">טוען הרשאות…</div>;
  }

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות במרחב המיומנויות והלמידה</div>;
  }

  if (agentQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  const agent = agentQuery.data;
  if (!agent) {
    const forbidden = agentQuery.error?.response?.status === 403;
    return (
      <div className="text-center py-16 space-y-2" dir="rtl">
        <p className="text-red-600 text-sm">
          {forbidden
            ? "אין הרשאה לצפות בעובד זה"
            : "לא ניתן לטעון את העובד — מרחב המיומנויות והלמידה אינו זמין"}
        </p>
        <Link to="/agents" className="text-xs text-brand-600 hover:underline">
          ← חזרה לעובדים
        </Link>
      </div>
    );
  }

  // ── Source state (null = not available; loading/error flags kept separately) ──

  const profile = profileQuery.data ?? null;
  const declared = profile?.declared ?? null;
  const proficiency = profile?.evaluated_proficiency ?? null;
  const goals = Array.isArray(profile?.learning_goals) ? profile.learning_goals : null;

  const experience = experienceQuery.data ?? null;
  const experienceSummary = experience?.summary ?? null;
  const entries = Array.isArray(experience?.entries) ? experience.entries : null;
  const regressions = entries ? regressionEntries(entries) : null;
  const candidates = entries ? knowledgeCandidates(entries) : null;

  const capabilities = workspaceQuery.data?.capabilities ?? null;
  const tools = Array.isArray(capabilities?.tools) ? capabilities.tools : null;
  const connections = Array.isArray(capabilities?.connections) ? capabilities.connections : null;
  const groups = tools ? toolGroups(tools).filter((g) => g.tools.length > 0) : null;

  const evaluation = evaluationQuery.data ?? null;
  const evaluationForbidden = evaluationQuery.error?.response?.status === 403;

  // The versioned skill/learning history rides on the agent detail response (learning_history);
  // null means the projection was not supplied — never "no changes".
  const learningHistory = agentQuery.data?.learning_history ?? null;
  const skillHistory = normalizeVersionedSkillHistory(learningHistory);
  const skillStateChanges = (skillHistory?.changes || []).filter(
    (change) => change.kind === "regression" || change.kind === "suspension" || change.kind === "reinstated",
  );

  const evidence = proficiency?.evidence ?? null;
  const openGoals = goals ? goals.filter((g) => g?.status === "open").length : null;

  // The avatar's own rule, read from the one place that owns it: this page prints the status as
  // text too, and a disabled employee must not read "זמין" beside the "העובד מושבת" note.
  const statusMeta = STATUS_META[drawnStatus(agent.availability_status, agent.is_enabled)];

  const healthyTiles =
    tools !== null && goals !== null && experienceSummary !== null && regressions !== null;

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Link to="/agents" className="hover:text-gray-600">עובדים</Link>
          <span>/</span>
          <Link to={`/agents/${id}`} className="hover:text-gray-600">{agent.name}</Link>
          <span>/</span>
          <span className="text-gray-600">מיומנויות ולמידה</span>
        </div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <EmployeeAvatar
              name={agent.name}
              avatarKey={agent.avatar_key}
              avatarColor={agent.avatar_color}
              status={agent.availability_status}
              isEnabled={agent.is_enabled}
              size="lg"
              showStatus
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 flex-wrap">
                מיומנויות ולמידה
                <span className="text-base font-medium text-gray-500">· {agent.name}</span>
              </h1>
              <p className="text-sm text-gray-500">
                {agent.role_title || "ללא תפקיד מוגדר"}
                {statusMeta && <span className="text-xs"> · {statusMeta.label}</span>}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              [agentQuery, profileQuery, experienceQuery, workspaceQuery, runsQuery, evaluationQuery]
                .forEach((q) => q.refetch())
            }
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
          >
            רענן
          </button>
        </div>
        <p className="text-sm text-gray-500">
          מה שאושר לעובד בפועל, מול מה שהוא מצהיר; מה נמדד מהעבודה שהתבצעה; ומה נלמד ממנה —
          כולל מה שאינו ידוע.
        </p>
      </header>

      {/* ── Overview ─────────────────────────────────────────────────────── */}

      <section aria-label="סקירת מיומנויות ולמידה" className="flex flex-wrap gap-3">
        <Tile
          label="כלים מאושרים לעובד"
          value={workspaceQuery.isLoading ? "…" : tools === null ? UNKNOWN : String(tools.length)}
          sub={groups && groups.length > 0 ? groups.map((g) => `${g.label}: ${g.tools.length}`).join(" · ") : "מכסת הכלים שהוקצתה"}
        />
        <Tile
          label="יעדי למידה"
          value={profileQuery.isLoading ? "…" : goals === null ? UNKNOWN : String(goals.length)}
          sub={openGoals != null ? `מהם פתוחים: ${openGoals}` : "פערי ידע שנצפו בריצות"}
        />
        <Tile
          label="עבודות מאומתות"
          value={experienceQuery.isLoading ? "…" : experienceSummary === null ? UNKNOWN : String(experienceSummary.verified_successes)}
          sub="אושרו על ידי טכנאי מתוך ההיסטוריה שנטענה"
        />
        <Tile
          label="נסיגות שתועדו"
          value={experienceQuery.isLoading ? "…" : regressions === null ? UNKNOWN : String(regressions.length)}
          sub="כשלים, דחיות או פעולות שנעצרו"
        />
      </section>
      {!healthyTiles && !profileQuery.isLoading && !experienceQuery.isLoading && !workspaceQuery.isLoading && (
        <p className="text-xs text-gray-400">
          חלק מהמקורות לא נטענו — "לא ידוע" משמעו שהנתון לא התקבל מהשרת, ולא אפס.
        </p>
      )}

      {/* ── Approved vs declared ─────────────────────────────────────────── */}

      <Section
        icon="🛡️"
        label="מיומנויות מאושרות מול הצהרות"
        hint="מאושר = מכסת הכלים והחיבורים שהוקצו לעובד בפועל, בכפוף לשערי האישור שלצד כל כלי. מוצהר = הכרזה של העובד בלבד — אינה הרשאה ואינה ראיה, ולכן אינה נספרת במאושר."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">מאושר ומוקצה לעובד</h3>
            {workspaceQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
            {!workspaceQuery.isLoading && workspaceQuery.isError && (
              <UnknownNote>
                {`${UNKNOWN} — לא ניתן לטעון את מיפוי ההרשאות (workspace); מכסת הכלים המאושרת אינה ידועה.`}
              </UnknownNote>
            )}
            {!workspaceQuery.isLoading && !workspaceQuery.isError && tools !== null && (
              <>
                {tools.length === 0 ? (
                  <p className="text-sm text-gray-400">לא הוקצו כלים מאושרים לעובד זה.</p>
                ) : (
                  <div className="space-y-2">
                    {groups.map((group) => (
                      <div key={group.key}>
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            title={group.title}
                            className={`text-[11px] px-1.5 py-0.5 rounded-full border ${group.chip}`}
                          >
                            {group.label} ({group.tools.length})
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {group.tools.slice(0, TOOL_MAX).map((t) => (
                            <span
                              key={`${group.key}:${t.name}`}
                              title={group.title}
                              className="px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700"
                            >
                              <MachineName>{boundedText(t.name, 60) || UNKNOWN}</MachineName>
                            </span>
                          ))}
                          {group.tools.length > TOOL_MAX && (
                            <span className="text-[11px] text-gray-400 self-center">
                              +{group.tools.length - TOOL_MAX} נוספים
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  חיבורים מוגדרים: {connections === null ? UNKNOWN : connections.length}
                  {connections && connections.length > 0 && (
                    <>
                      {" — "}
                      {connections
                        .slice(0, CONNECTION_MAX)
                        .map((c) => boundedText(c?.display_name, 40) || boundedText(c?.system_type, 40) || "חיבור ללא שם")
                        .map((name, i) => (
                          <span key={i}>
                            {i > 0 && " · "}
                            <MachineName>{name}</MachineName>
                          </span>
                        ))}
                      {connections.length > CONNECTION_MAX && ` · +${connections.length - CONNECTION_MAX}`}
                    </>
                  )}
                  {" · "}
                  <Link to={`/agents/${id}`} className="text-blue-700 hover:underline">
                    סטטוס החיבורים ומכסת הכלים המלאה — בעמוד העובד
                  </Link>
                </p>
              </>
            )}
          </div>

          <div data-testid="declared-specialties" className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-violet-800">מוצהר בלבד</h3>
              <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                מוצהר
              </span>
            </div>
            <p className="text-[11px] text-violet-700">
              הצהרת העובד על תחומי ההתמחות שלו — אינה הרשאה, אינה אימות ואינה נספרת כמאושרת.
            </p>
            {profileQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
            {!profileQuery.isLoading && profileQuery.isError && (
              <UnknownNote>{`${UNKNOWN} — לא ניתן לטעון את הפרופיל; המיומנויות המוצהרות אינן ידועות.`}</UnknownNote>
            )}
            {!profileQuery.isLoading && !profileQuery.isError && declared && (
              <>
                {Array.isArray(declared.specialties) && declared.specialties.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {declared.specialties.slice(0, TOOL_MAX).map((s, i) => (
                      <span
                        key={`${i}:${s}`}
                        className="px-1.5 py-0.5 rounded border border-violet-200 bg-violet-100 text-violet-800 text-xs"
                      >
                        {boundedText(s, 60)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">לא הוצהרו תחומי התמחות.</p>
                )}
                {boundedText(declared.expertise_statement, 400) && (
                  <p className="text-sm text-gray-700">{boundedText(declared.expertise_statement, 400)}</p>
                )}
              </>
            )}
          </div>
        </div>
      </Section>

      {/* ── Versions & evaluation ────────────────────────────────────────── */}

      <Section
        icon="📊"
        label="גרסאות והערכת מיומנות"
        hint="יומן הגרסאות של המיומנות (מספר גרסה, סיבת השינוי, הראיה והגורם) מוצג בסעיף שאחרי תמונת ההערכה; התמונה שלהלן היא מצב ההערכה הנוכחי להשוואה מול ההצהרה."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div data-testid="evaluated-proficiency" className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-blue-800">תמונת הערכה נוכחית</h3>
              <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                נמדד
              </span>
            </div>
            {profileQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
            {!profileQuery.isLoading && profileQuery.isError && (
              <UnknownNote>{`${UNKNOWN} — לא ניתן לטעון את תמונת ההערכה.`}</UnknownNote>
            )}
            {!profileQuery.isLoading && !profileQuery.isError && proficiency && (
              <>
                <p className="text-xs text-gray-500">
                  בסיס: {EVALUATION_BASIS_LABELS[proficiency.basis] || boundedText(proficiency.basis, 40) || UNKNOWN}
                  {fmt(proficiency.evaluated_at) && ` · נכון ל-${fmt(proficiency.evaluated_at)}`}
                </p>
                {proficiency.has_outcome_evidence === false && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    טרם הצטברו ראיות תוצאה (ריצות שהסתיימו) — ציוני המיומנות אינם מחושבים; הם מוצגים
                    כ"טרם ידוע", לא כאפס.
                  </p>
                )}
                <div className="text-sm text-gray-700 space-y-0.5">
                  <div>
                    שיעור הצלחה מתוך ריצות שהסתיימו:{" "}
                    <span className="font-medium">{formatRate(proficiency.resolution_rate) || "טרם ידוע"}</span>
                  </div>
                  <div>
                    ציון מוערך כולל: <span className="font-medium">{formatScore(proficiency.overall_score) || "טרם ידוע"}</span>
                  </div>
                </div>
                {evidence && (
                  <p className="text-xs text-gray-500">
                    ריצות: סה"כ {evidence.total_runs ?? UNKNOWN} · הסתיימו {evidence.terminal_runs ?? UNKNOWN} ·
                    הושלמו {evidence.completed_runs ?? UNKNOWN} · נכשלו {evidence.failed_runs ?? UNKNOWN} ·
                    בטיפול {evidence.pending_runs ?? UNKNOWN}
                  </p>
                )}
                {Array.isArray(proficiency.by_category) && proficiency.by_category.length > 0 ? (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-600">לפי סוג עבודה</div>
                    {proficiency.by_category.slice(0, TOOL_MAX).map((c, i) => (
                      <div key={`${i}:${c.category}`} className="flex items-center gap-2 text-xs text-gray-600">
                        <MachineName>{boundedText(c.category, 40) || UNKNOWN}</MachineName>
                        <span>
                          הושלמו {c.completed ?? UNKNOWN} מתוך {c.total ?? UNKNOWN}
                        </span>
                        <span className="text-gray-500">{formatRate(c.completion_rate) || "טרם ידוע"}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">אין פירוט לפי סוג עבודה.</p>
                )}
              </>
            )}
            {!profileQuery.isLoading && !profileQuery.isError && !proficiency && (
              <UnknownNote>{`${UNKNOWN} — תמונת ההערכה לא סופקה על ידי השרת.`}</UnknownNote>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">הערכה מפורטת (הרשאת מנהל)</h3>
            <p className="text-[11px] text-gray-500">
              דוח הערכת המיומנות המלא מוגש למנהלים בלבד; ללא הרשאה, יתר המסך אינו תלוי בו.
            </p>
            {evaluationQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
            {!evaluationQuery.isLoading && evaluationForbidden && (
              <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                ההערכה המפורטת אינה חשופה בסשן זה — נדרשת הרשאת מנהל. הנתונים אינם מוצגים, ואינם אפס.
              </p>
            )}
            {!evaluationQuery.isLoading && !evaluationForbidden && evaluationQuery.isError && (
              <UnknownNote>{`${UNKNOWN} — לא ניתן לטעון את ההערכה המפורטת.`}</UnknownNote>
            )}
            {!evaluationQuery.isLoading && !evaluationQuery.isError && evaluation && (
              <>
                <p className="text-xs text-gray-500">
                  {fmt(evaluation.evaluated_at) ? `נכון ל-${fmt(evaluation.evaluated_at)}` : "מועד ההערכה אינו ידוע"}
                  {evaluation.agent_enabled === false && " · העובד מושבת"}
                </p>
                {evaluation.has_outcome_evidence === true ? (
                  <div className="text-sm text-gray-700 space-y-0.5">
                    <div>
                      שיעור הצלחה: <span className="font-medium">{formatRate(evaluation.resolution_rate) || "טרם ידוע"}</span>
                    </div>
                    <div>
                      ציון כולל: <span className="font-medium">{formatScore(evaluation.overall_score) || "טרם ידוע"}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    הדוח אינו נושא ציון: אין ריצה שהסתיימה עבור עובד זה, ולכן אין תוצאה למדוד. המונה
                    שלהלן מתאר עבודה שבטיפול בלבד.
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  ריצות: סה"כ {evaluation.total_runs ?? UNKNOWN} · הושלמו {evaluation.completed_runs ?? UNKNOWN} ·
                  נכשלו {evaluation.failed_runs ?? UNKNOWN} · בטיפול {evaluation.pending_runs ?? UNKNOWN}
                  {typeof evaluation.avg_duration_s === "number" &&
                    ` · משך ממוצע ${Math.round(evaluation.avg_duration_s)} שנ׳`}
                  {typeof evaluation.avg_cost_usd === "number" &&
                    ` · עלות ממוצעת $${evaluation.avg_cost_usd.toFixed(4)}`}
                </p>
                {evaluation.has_outcome_evidence === true &&
                  Array.isArray(evaluation.skill_breakdown) && evaluation.skill_breakdown.length > 0 && (
                  <div className="space-y-1">
                    {evaluation.skill_breakdown.slice(0, TOOL_MAX).map((row, i) => (
                      <div key={`${i}:${row.category}`} className="flex items-center gap-2 text-xs text-gray-600">
                        <MachineName>{boundedText(row.category, 40) || UNKNOWN}</MachineName>
                        <span>
                          הושלמו {row.completed ?? UNKNOWN} מתוך {row.total ?? UNKNOWN}
                        </span>
                        <span className="text-gray-500">{formatRate(row.completion_rate) || "טרם ידוע"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Section>

      {/* ── Versioned skill history ──────────────────────────────────────── */}

      <SkillVersionHistorySection
        history={learningHistory}
        isLoading={agentQuery.isLoading}
        isError={agentQuery.isError}
      />

      {/* ── Proficiency evidence ─────────────────────────────────────────── */}

      <Section
        icon="🧪"
        label="עדויות מיומנות"
        hint={`הערכה נגזרת מהריצות המתועדות. הטבלה מציגה עדויות מצטברות; הטיימליין מציג את רשומות העבודה עצמן — ${EVIDENCE_RUNS_LIMIT} הריצות האחרונות שנטענו.`}
      >
        {profileQuery.isError && !profileQuery.isLoading ? (
          <UnknownNote>{`${UNKNOWN} — עדויות ההערכה המצטברות לא נטענו.`}</UnknownNote>
        ) : proficiency && evidence ? (
          <p className="text-sm text-gray-700">
            סה"כ ריצות: {evidence.total_runs ?? UNKNOWN} · הסתיימו: {evidence.terminal_runs ?? UNKNOWN} ·
            הושלמו: {evidence.completed_runs ?? UNKNOWN} · נכשלו: {evidence.failed_runs ?? UNKNOWN} ·
            בטיפול: {evidence.pending_runs ?? UNKNOWN}
          </p>
        ) : (
          <UnknownNote>{`${UNKNOWN} — עדויות ההערכה המצטברות לא סופקו.`}</UnknownNote>
        )}
        <ExperienceTimeline
          runs={Array.isArray(runsQuery.data) ? runsQuery.data : []}
          isLoading={runsQuery.isLoading}
          isError={runsQuery.isError}
        />
      </Section>

      {/* ── Knowledge candidates ─────────────────────────────────────────── */}

      <Section
        icon="💡"
        label="מועמדים לידע"
        hint="לקחים שתועדו מול הריצות — תיקוני טכנאים, הערות כשל ודחייה ופתרונות מאומתים. כל פריט מצביע על הרשומה שממנה בא."
        count={candidates === null ? undefined : candidates.length}
      >
        {experienceQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
        {!experienceQuery.isLoading && experienceQuery.isError && (
          <UnknownNote>{`${UNKNOWN} — היסטוריית הניסיון לא נטענה; מועמדים לידע אינם ידועים.`}</UnknownNote>
        )}
        {!experienceQuery.isLoading && !experienceQuery.isError && candidates !== null && candidates.length === 0 && (
          <p className="text-sm text-gray-400">
            לא תועדו מועמדים לידע ברשומות שנטענו ({(entries ?? []).length} רשומות).
          </p>
        )}
        {candidates !== null && candidates.length > 0 && (
          <>
            <ol className="space-y-2">
              {candidates.slice(0, CANDIDATE_MAX).map(({ entry, lesson }, i) => {
                const kindMeta = LESSON_KIND_META[lesson.kind] || null;
                const summary = boundedText(lesson.summary, 160) || "לא תועדה תמצית לפריט זה";
                const ticket = entry?.ticket || null;
                const runId = runIdOf(entry);
                return (
                  <li key={`${entry?.entry_id ?? i}:${i}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${kindMeta ? kindMeta.cls : "bg-gray-100 text-gray-600"}`}>
                        {kindMeta ? kindMeta.label : boundedText(lesson.kind, 40) || "לקח"}
                      </span>
                      <span className="text-[11px] text-gray-400">{fmt(entryTimestamp(entry)) || "מועד לא ידוע"}</span>
                    </div>
                    <p className="text-sm text-gray-800">{summary}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      {runId && (
                        <Link
                          to={`/agent-runs/${runId}`}
                          className="text-blue-600 hover:underline"
                          aria-label={`פתח את הריצה${ticket?.ticket_number ? ` של קריאה #${ticket.ticket_number}` : ""}`}
                        >
                          צפה בריצה
                        </Link>
                      )}
                      {ticket?.id && (
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="text-blue-600 hover:underline"
                          aria-label={`פתח את הקריאה${ticket.ticket_number ? ` #${ticket.ticket_number}` : ""}`}
                        >
                          {ticket.ticket_number ? `קריאה #${ticket.ticket_number}` : "קריאה מקושרת"}
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            {candidates.length > CANDIDATE_MAX && (
              <p className="text-xs text-gray-500">
                מוצגים {CANDIDATE_MAX} מתוך {candidates.length} מועמדים — הוותיקים אינם מוצגים.
              </p>
            )}
            <p className="text-xs text-gray-500">
              מועמדים לידע ברמת הארגון (מקבצי קריאות סגורות) מנוהלים ב-
              <Link to="/knowledge" className="text-blue-700 hover:underline">עמוד ניהול הידע</Link>.
            </p>
          </>
        )}
      </Section>

      {/* ── Learning goals ───────────────────────────────────────────────── */}

      <Section
        icon="🎯"
        label="יעדי למידה"
        hint="פערי ידע שתועדו בריצות העובד ונעקבים כיעדי למידה — היעדים אינם הרשאות ואינם משנים את מכסת הכלים."
        count={goals === null ? undefined : goals.length}
      >
        {profileQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
        {!profileQuery.isLoading && profileQuery.isError && (
          <UnknownNote>{`${UNKNOWN} — לא ניתן לטעון את יעדי הלמידה.`}</UnknownNote>
        )}
        {!profileQuery.isLoading && !profileQuery.isError && goals !== null && goals.length === 0 && (
          <p className="text-sm text-gray-400">לא זוהו פערי ידע או יעדי למידה עבור עובד זה.</p>
        )}
        {goals !== null && goals.length > 0 && (
          <ol className="space-y-2">
            {goals.slice(0, TOOL_MAX).map((goal, i) => {
              const statusMetaGoal = learningStatusMeta(goal?.status);
              return (
                <li key={goal?.goal_id ?? i} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      title={statusMetaGoal.title}
                      className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                        goal?.status === "open"
                          ? "bg-amber-100 text-amber-800"
                          : goal?.status === "acknowledged"
                            ? "bg-teal-100 text-teal-700"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {statusMetaGoal.label}
                    </span>
                    {boundedText(goal?.category, 40) && (
                      <span className="text-[11px] text-gray-500">
                        <MachineName>{boundedText(goal.category, 40)}</MachineName>
                      </span>
                    )}
                    {Number.isInteger(goal?.occurrence_count) && (
                      <span className="text-[11px] text-gray-500">זוהה ב-{goal.occurrence_count} ריצות</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800">{boundedText(goal?.topic, 160) || "יעד ללא כותרת"}</p>
                  <p className="text-[11px] text-gray-400">
                    {fmt(goal?.first_seen_at) ? `נצפה לראשונה: ${fmt(goal.first_seen_at)}` : "מועד ראשון לא ידוע"}
                    {fmt(goal?.last_seen_at) && ` · לאחרונה: ${fmt(goal.last_seen_at)}`}
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </Section>

      {/* ── Regressions / recovery ───────────────────────────────────────── */}

      <Section
        icon="↩️"
        label="נסיגות והיסטוריית שחזור"
        hint="שני מסלולים מתועדים: שינויי מצב מיומנות (נסיגה, השהיה או חזרה לפעילות) מתוך יומן הגרסאות — עם הגרסה, הסיבה והראיה — ולצדם המסלול ברמת העבודות: עבודות שנכשלו או נדחו ופעולות שהוצעו ונעצרו."
        count={regressions === null ? undefined : regressions.length}
      >
        {/* Skill-state regressions/suspensions from the versioned ledger — the topical slice */}
        <div data-testid="skill-state-changes" className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
          <h3 className="text-sm font-semibold text-gray-700">שינויי מצב מיומנות (נסיגה / השהיה / חזרה לפעילות)</h3>
          {agentQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
          {!agentQuery.isLoading && agentQuery.isError && (
            <UnknownNote>{`${UNKNOWN} — יומן הגרסאות של המיומנות לא נטען; שינויי מצב אינם ידועים.`}</UnknownNote>
          )}
          {!agentQuery.isLoading && !agentQuery.isError && skillHistory === null && (
            <UnknownNote>
              {`${UNKNOWN} — יומן הגרסאות של המיומנות לא סופק על ידי המערכת; אין להסיק מכך שלא תועדו נסיגות או השהיות.`}
            </UnknownNote>
          )}
          {!agentQuery.isLoading && !agentQuery.isError && skillHistory !== null && skillStateChanges.length === 0 && (
            <p className="text-sm text-gray-400">
              ביומן הגרסאות שנטען לא תועדו שינויי מצב מיומנות (נסיגה, השהיה או חזרה לפעילות).
            </p>
          )}
          {!agentQuery.isError && skillStateChanges.length > 0 && (
            <>
              <ul className="space-y-2">
                {skillStateChanges.slice(0, SKILL_STATE_MAX).map((change, i) => (
                  <SkillChangeEntry key={`${change.subdomain}:${change.version}:${i}`} change={change} />
                ))}
              </ul>
              {skillStateChanges.length > SKILL_STATE_MAX && (
                <p className="text-xs text-gray-500">
                  מוצגים {SKILL_STATE_MAX} השינויים האחרונים מתוך {skillStateChanges.length} שינויי מצב
                  בחלון שנטען.
                </p>
              )}
            </>
          )}
          <p className="text-[11px] text-gray-400">
            מקור: יומן הגרסאות המגורסם (היסטוריית למידה) — תיעוד בלבד, אינו מרחיב הרשאות ואינו מבצע
            פעולות. היומן המלא מוצג בסעיף "היסטוריית גרסאות מיומנות" שלמעלה.
          </p>
        </div>
        {experienceQuery.isLoading && <p className="text-sm text-gray-400">טוען…</p>}
        {!experienceQuery.isLoading && experienceQuery.isError && (
          <UnknownNote>{`${UNKNOWN} — היסטוריית הניסיון לא נטענה; נסיגות אינן ידועות.`}</UnknownNote>
        )}
        {!experienceQuery.isLoading && !experienceQuery.isError && experienceSummary && (
          <p className="text-sm text-gray-700">
            מתוך {(entries ?? []).length} הרשומות שנטענו תועדו {experienceSummary.failures ?? UNKNOWN} כשלים
            ו-{experienceSummary.cancellations ?? UNKNOWN} דחיות/עצירות.
          </p>
        )}
        {regressions !== null && regressions.length === 0 && (
          <p className="text-sm text-gray-400">לא תועדו כשלים, דחיות או פעולות שנעצרו ברשומות שנטענו.</p>
        )}
        {regressions !== null && regressions.length > 0 && (
          <>
            <ol className="space-y-2">
              {regressions.slice(0, REGRESSION_MAX).map((entry) => {
                const meta = ENTRY_OUTCOME_META[entry.outcome] || null;
                const summary =
                  boundedText(entry.summary, 200) || (meta ? meta.fallback : "לא תועדה תמצית");
                const stopped = stoppedActions(entry);
                const actions = entry.actions || null;
                const ticket = entry.ticket || null;
                const runId = runIdOf(entry);
                return (
                  <li key={entry.entry_id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${meta ? meta.cls : "bg-gray-100 text-gray-600"}`}>
                        {meta ? `${meta.icon} ${meta.label}` : RUN_LABELS[entry.kind] || "רשומה"}
                      </span>
                      {stopped > 0 && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                          {stopped} פעולות נעצרו
                        </span>
                      )}
                      <span className="text-[11px] text-gray-400 mr-auto">
                        {fmt(entryTimestamp(entry)) || "מועד לא ידוע"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800">{summary}</p>
                    {actions && (
                      <p className="text-[11px] text-gray-500">
                        פעולות שהוצעו: {actions.total ?? UNKNOWN} · בוצעו: {actions.succeeded ?? UNKNOWN} ·
                        נכשלו: {actions.failed ?? UNKNOWN} · נדחו: {actions.rejected ?? UNKNOWN}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      {runId && (
                        <Link
                          to={`/agent-runs/${runId}`}
                          className="text-blue-600 hover:underline"
                          aria-label={`פתח את הריצה${ticket?.ticket_number ? ` של קריאה #${ticket.ticket_number}` : ""} — טיפול חוזר כפוף לשערים הקיימים`}
                        >
                          פתח ריצה לטיפול
                        </Link>
                      )}
                      {ticket?.id && (
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="text-blue-600 hover:underline"
                          aria-label={`פתח את הקריאה${ticket.ticket_number ? ` #${ticket.ticket_number}` : ""}`}
                        >
                          {ticket.ticket_number ? `קריאה #${ticket.ticket_number}` : "קריאה מקושרת"}
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            {regressions.length > REGRESSION_MAX && (
              <p className="text-xs text-gray-500">
                מוצגות {REGRESSION_MAX} הרשומות האחרונות מתוך {regressions.length}.
              </p>
            )}
          </>
        )}
      </Section>

      <footer className="flex flex-wrap gap-4 text-xs text-gray-500 pb-4">
        <Link to={`/agents/${id}/cv`} className="text-blue-700 hover:underline">
          הפרופיל המקצועי · CV
        </Link>
        <Link to={`/agents/${id}`} className="text-blue-700 hover:underline">
          חזרה לעמוד העובד — מרחב העבודה והניסיון
        </Link>
        <Link to="/knowledge" className="text-blue-700 hover:underline">
          ספריית הידע הארגונית
        </Link>
        <Link to="/agent-runs" className="text-blue-700 hover:underline">
          כל הריצות
        </Link>
      </footer>
    </div>
  );
}
